import { Box, Typography } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { useLicensingServiceV1 } from "@rw1/api-services";
import { CompanyData_V2, Licensing_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useDeferredValue, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ConfirmationDialog,
  DEFAULT_RENEWAL_DAYS,
  ExpirationDatePicker,
  RenewalPicker,
  SearchBar,
  SeatPicker,
  SelectableList,
  SelectableListItem,
} from "../../../components";
import { useCompanySettings } from "../../../hooks";
import { useAllExperiences } from "../../../hooks/useAllExperiences";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { ALL_LICENSES_QUERY_KEY } from "../hooks/useExternalLicenses";
import { useIsWhitelistEnabled } from "../hooks/useWhitelistFeatureFlag";
import { WhitelistSwitch } from "./WhitelistSwitch";

interface GrantLicenseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  existingLicensesReleaseChannelIds?: string[];
  assignedOrganization: CompanyData_V2.Company;
  assignedOrganizationKey: string;
}

export const GrantLicenseDialog: FC<GrantLicenseDialogProps> = ({
  isOpen,
  onClose,
  existingLicensesReleaseChannelIds = [],
  assignedOrganization,
  assignedOrganizationKey,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const licensingService = useLicensingServiceV1();
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [selectedReleaseChannelId, setSelectedReleaseChannelId] = useState<
    string | null
  >(null);
  const [checked, setChecked] = useState(true);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [seats, setSeats] = useState<number | null>(null);
  const [isUnlimitedSeats, setIsUnlimitedSeats] = useState(true);
  const [renewalDays, setRenewalDays] = useState<number | null>(
    DEFAULT_RENEWAL_DAYS,
  );
  const [hasWhitelist, setHasWhitelist] = useState(false);
  const isWhitelistEnabled = useIsWhitelistEnabled();
  const { companyId } = useParams();
  const { isSeatRenewalEnabled } = useCompanySettings({ companyId });

  const { experiences } = useAllExperiences();

  const isMobile = useIsMobile();

  const createLicenseMutation = useMutation({
    mutationFn: async (licenseData: Licensing_V1.NewLicenseObject) => {
      return await licensingService.createLicense(licenseData);
    },
    onSuccess: (newLicense) => {
      // Add the newly created license to the cache
      queryClient.setQueryData(
        [ALL_LICENSES_QUERY_KEY, companyId],
        (oldData: Licensing_V1.License[] | undefined) => {
          if (!oldData) return [newLicense];

          return [...oldData, newLicense];
        },
      );

      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
    },
  });

  const mappedItems = useMemo(() => {
    const hasAccessMap = existingLicensesReleaseChannelIds.reduce<
      Record<string, boolean>
    >((acc, id) => {
      acc[id] = true;

      return acc;
    }, {});

    const internalExperiences = experiences.filter(
      (exp) => exp.companyId === companyId,
    );

    const sortedExperiences = [...internalExperiences].sort((a, b) => {
      return a.friendlyName.localeCompare(b.friendlyName);
    });

    return sortedExperiences.map<SelectableListItem>((experience) => {
      const hasAccess = !!hasAccessMap[experience.releaseChannelId];

      const tertiaryText = hasAccess
        ? t("licenses.existingLicense")
        : undefined;
      const type = hasAccess ? "hasAccess" : "selectable";

      return {
        id: experience.releaseChannelId,
        primaryText: experience.friendlyName,
        secondaryText: experience.companyName ?? "-",
        tertiaryText,
        type,
      };
    });
  }, [
    existingLicensesReleaseChannelIds,
    experiences,
    companyId,
    t,
  ]);

  const selectableItems = useMemo(() => {
    return deferredSearchTerm
      ? mappedItems.filter((item) =>
        `${item.primaryText}`
          .toLowerCase()
          .includes(deferredSearchTerm.toLowerCase()),
      )
      : mappedItems;
  }, [mappedItems, deferredSearchTerm]);

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
  };

  const handleSearchClear = () => {
    setSearchTerm("");
  };

  const handleReleaseChannelSelect = (id: string) => {
    setSelectedReleaseChannelId(id);
  };

  const handleGrantLicense = async () => {
    if (!selectedReleaseChannelId || !companyId) return;

    // Only include renewal period if renewal is enabled, seats are limited, and a valid renewal period is set
    const shouldIncludeRenewal =
      isSeatRenewalEnabled && !isUnlimitedSeats && renewalDays;

    // Calculate renewal date if renewal is enabled
    const renewalDate = shouldIncludeRenewal
      ? new Date(Date.now() + renewalDays * 24 * 60 * 60 * 1000)
      : new Date(Date.now() + DEFAULT_RENEWAL_DAYS * 24 * 60 * 60 * 1000);

    const licenseData = new Licensing_V1.NewLicenseObject({
      releaseChannelId: selectedReleaseChannelId,
      assignedOrganizationKey,
      licenseName: `${selectedItems[0]?.primaryText}`,
      issuingOrganizationId: companyId,
      seats: isUnlimitedSeats ? undefined : (seats ?? undefined),
      expiresAt: checked ? undefined : (expirationDate ?? undefined),
      renewalPeriodDays: shouldIncludeRenewal
        ? renewalDays
        : DEFAULT_RENEWAL_DAYS,
      renewalDate,
      hasWhitelist,
    });

    await createLicenseMutation.mutateAsync(licenseData);
    setSelectedReleaseChannelId(null);
    handleSearchClear();
    setExpirationDate(null);
    setSeats(null);
    setIsUnlimitedSeats(true);
    setRenewalDays(DEFAULT_RENEWAL_DAYS);
    setChecked(false);
    setHasWhitelist(false);
    onClose();
  };

  const handleUnselect = () => {
    setSelectedReleaseChannelId(null);
    setExpirationDate(null);
    setSeats(null);
    setIsUnlimitedSeats(true);
    setRenewalDays(DEFAULT_RENEWAL_DAYS);
  };

  const handleCancel = () => {
    setSelectedReleaseChannelId(null);
    handleSearchClear();
    setExpirationDate(null);
    setSeats(null);
    setIsUnlimitedSeats(true);
    setRenewalDays(DEFAULT_RENEWAL_DAYS);
    setChecked(false);
    setHasWhitelist(false);
    onClose();
  };

  const actionText = useMemo(
    () => (isMobile ? t("licenses.grant") : t("licenses.grantLicense")),
    [isMobile, t],
  );

  const isExternalExperience = useMemo(() => {
    const selectedExperience = experiences.find(
      (exp) => exp.releaseChannelId === selectedReleaseChannelId,
    );

    return selectedExperience?.companyId !== companyId;
  }, [companyId, experiences, selectedReleaseChannelId]);

  const selectedItems = useMemo(() => {
    const selectedItem = mappedItems.find(
      (item) => item.id === selectedReleaseChannelId,
    );

    return selectedItem ? [selectedItem] : [];
  }, [mappedItems, selectedReleaseChannelId]);

  const isGrantDisabled =
    !selectedReleaseChannelId ||
    createLicenseMutation.isPending ||
    (!checked && !expirationDate) ||
    (!isUnlimitedSeats && (seats === null || seats === 0)) ||
    (isSeatRenewalEnabled &&
      !isUnlimitedSeats &&
      (renewalDays === null || renewalDays <= 0 || renewalDays > 365));

  return (
    <ConfirmationDialog.Root
      fullScreenOnMobile={true}
      fullWidth
      height="828px"
      maxWidth="xs"
      onClose={handleCancel}
      open={isOpen}
    >
      <ConfirmationDialog.Title showIcon={false}>
        <Typography
          sx={{
            fontSize: { xs: 18, md: 24 },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 700,
          }}
        >
          {t("licenses.grantLicenseTo", {
            companyName: assignedOrganization.friendlyName,
          })}
        </Typography>
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <SearchBar
            handleClear={handleSearchClear}
            handleSubmit={handleSearchSubmit}
            onChange={handleSearchChange}
            placeholder={t("licenses.searchExperiences")}
            sx={{ maxWidth: undefined }}
            value={searchTerm}
          />
        </form>
        <SelectableList
          emptyMessage={t("licenses.noExperiencesAvailable")}
          height="100%"
          items={selectableItems}
          maxHeight="100%"
          minHeight={0}
          multiSelect={false}
          onChipRemove={handleUnselect}
          onItemSelect={handleReleaseChannelSelect}
          selectedId={selectedReleaseChannelId}
          selectedItems={selectedItems}
          selectedItemsTitle={t("licenses.selectedExperience")}
          sx={{ flex: 1, minHeight: 0 }}
        />
        <Box
          sx={{
            visibility: selectedReleaseChannelId ? "visible" : "hidden",
            display: "flex",
            flexDirection: "row",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 0.5 }}>
            <SeatPicker
              checked={isUnlimitedSeats}
              handleSwitchChange={(isChecked) => setIsUnlimitedSeats(isChecked)}
              label={t("licenses.seats")}
              labelWidth={90}
              min={null}
              mobileLabel={t("licenses.seatLimit")}
              onChange={setSeats}
              showCompactView
              showTooltip
              value={seats}
            />
          </Box>
          <Box sx={{ flex: 0.5 }}>
            <ExpirationDatePicker
              checked={checked}
              isExternal={isExternalExperience}
              key={selectedReleaseChannelId ? "active" : "inactive"}
              onChange={setExpirationDate}
              setChecked={setChecked}
              showCompactView
              showTooltip
              value={expirationDate}
            />
          </Box>
        </Box>
        {/* Renewal field - only shown when seat renewal is enabled and seats are limited */}
        {(isSeatRenewalEnabled || isWhitelistEnabled) && (
          <Box
            sx={{
              visibility:
                selectedReleaseChannelId &&
                  (isWhitelistEnabled || !isUnlimitedSeats)
                  ? "visible"
                  : "hidden",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 2,
              mt: 2,
              pb: 3,
            }}
          >
            {isWhitelistEnabled && (
              <WhitelistSwitch
                checked={hasWhitelist}
                onChange={setHasWhitelist}
              />
            )}
            {isSeatRenewalEnabled && (
              <Box
                sx={{
                  flex: 0.5,
                  visibility:
                    selectedReleaseChannelId && !isUnlimitedSeats
                      ? "visible"
                      : "hidden",
                }}
              >
                <RenewalPicker
                  disabled={isUnlimitedSeats}
                  label={t("licenses.renewal")}
                  onChange={setRenewalDays}
                  showMobileLayout
                  showTooltip
                  value={renewalDays}
                />
              </Box>
            )}
          </Box>
        )}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("licenses.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isGrantDisabled}
          isLoading={createLicenseMutation.isPending}
          loadingText={t("licenses.granting")}
          onClick={() => void handleGrantLicense()}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
